import React, { useRef } from "react";
import { Box, Button, Flex, Group, HoverCard } from "@mantine/core";
import { IconFilter } from "@tabler/icons";
import IdeasTabs from "../IdeasTabs";
import SearchPanel from "./SearchPanel";
import SavedFiltersTooltip from "./sticky-filters/SavedFiltersTooltip";
import { useFiltersContextHelper } from "../filters/useFiltersContextHelper";
import { useIdeaFilters } from "../../../hooks/useIdeaFilters";

interface HeaderProps {
  savedViews: any[];
  selectedView: any;
  setSelectedView: (value: any) => void;
  upsertView: (body: any, callback: (res: any) => void) => void;
  warpWidth: number;
  actionIconsWidth: number; 
  isArchived: boolean;  
  isGlobal: boolean;
  setIsGlobal: (isGlobal: boolean) => void;
  isSubPageView: boolean;
  setIsSubPageView: (isSubPageView: boolean) => void;
  toggle: () => void;
  search: string;
  setSearch: (search: string) => void;
  searchOpened: boolean;
  setSearchOpened: (opened: boolean) => void;
  actionIconsRef: React.MutableRefObject<any>;
  setFilterModalOpened: (opened: boolean) => void;
  settingsMenu?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({
  savedViews,
  selectedView,
  setSelectedView,
  upsertView,
  warpWidth,
  actionIconsWidth,
  isArchived,
  isGlobal,
  setIsGlobal,
  isSubPageView,
  setIsSubPageView,
  toggle,
  search,
  setSearch,
  searchOpened,
  setSearchOpened,
  actionIconsRef,
  setFilterModalOpened,
  settingsMenu,
}) => {
  const containerRef = useRef(null);
  const { activeFilters } = useFiltersContextHelper();
  const { ideasFiltersConfig } = useIdeaFilters();
  const activeFilterCount = (activeFilters ?? []).filter((f: any) => f.selected !== undefined).length;

  return (
    <Group
      ref={containerRef}
      id="tabs-header"
      noWrap
      mb="md"
      position="apart"
      sx={{ whiteSpace: "nowrap" }}
    >
      <Box pb="0" sx={{ whiteSpace: "nowrap" }} mt={3}>
        <IdeasTabs
          savedViews={savedViews}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          upsertViews={upsertView}
          isGlobal={isGlobal}
          setIsGlobal={setIsGlobal}
          isSubPageView={isSubPageView}
          setIsSubPageView={setIsSubPageView}
          SplitOn={Math.floor((warpWidth - actionIconsWidth) / 150)}
          containerRef={containerRef} 
          isArchived={isArchived}
        />
      </Box>
      <Flex ref={actionIconsRef} align="center" gap={8}>
        <SearchPanel
          toggle={toggle}
          search={search}
          setSearch={setSearch}
          searchOpened={searchOpened}
          setSearchOpened={setSearchOpened}
        />
        <Box sx={{ position: 'relative' }}>
          <HoverCard position="bottom-end">
            <HoverCard.Target>
              <Button
                variant="outline"
                leftIcon={<IconFilter size={16} />}
                onClick={() => setFilterModalOpened(true)}
                size="sm"
                sx={{
                  height: 32,
                  borderColor: "#D8D8DB",
                  color: "black",
                  fontSize: 12,
                  fontWeight: 500,
                  ":hover": {
                    backgroundColor: "#5C5CEB1A"
                  }
                }}
              >
                Filters
              </Button>
            </HoverCard.Target>
            <HoverCard.Dropdown
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <SavedFiltersTooltip filters={activeFilters ?? []} filtersConfig={ideasFiltersConfig} />
            </HoverCard.Dropdown>
          </HoverCard>
          {activeFilterCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -5,
                right: -6,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#5C5CEB',
                color: '#fff',
                fontSize: 10,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </Box>
        {settingsMenu}
      </Flex>
    </Group>
  );
};

export default Header;
